import React, { useEffect, useState } from 'react';
import { LOGIN_URL, TEST_ENDPOINT } from '~src/config';
import { Loading } from '~src/components/Loading';
import { Error } from '~src/components/Error';

function attemptLogin() {
  // see https://developer.atlassian.com/platform/bifrost/config/user-login-to-fe-apps/#login-with-your-frontend-application
  window.location.href = `${LOGIN_URL}?continue=${encodeURIComponent(
    globalThis.location.href,
  )}`;
}

const checkLoggedIn = async () => {
  try {
    const res = await fetch(TEST_ENDPOINT);

    if (res.status === 401 || res.status === 404) {
      return false;
    }

    if (res.status >= 500) {
      throw res.status;
    }

    return true;
  } catch (err) {
    console.error(`Error checking if user is logged in`);
    throw err;
  }
};

type Props = {
  component: React.ReactElement;
};

export const AuthWrapper = ({ component }: Props) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  function getNextRetryTimeoutMs() {
    let nextRetryTimeoutMs = 10000;
    const lastAttemptString = sessionStorage.getItem('loginLastAttempt');
    const lastAttemptDate =
      lastAttemptString == null ? new Date(0) : new Date(lastAttemptString);
    lastAttemptDate.setSeconds(lastAttemptDate.getSeconds() + 10);
    if (lastAttemptDate <= new Date()) {
      nextRetryTimeoutMs = 1;
    }
    sessionStorage.setItem('loginLastAttempt', new Date().toString());
    return nextRetryTimeoutMs;
  }

  useEffect(() => {
    console.log('Check logged in status');
    const loginStatus = sessionStorage.getItem('attemptedLoginCheck');
    if (!loginStatus) {
      // without it react goes bonkers and refreshes after each rendering
      sessionStorage.setItem('attemptedLoginCheck', 'true');
      setLoading(true);
      checkLoggedIn()
        .then((loggedIn) => {
          if (loggedIn) {
            setLoading(false);
          } else {
            setTimeout(() => {
              sessionStorage.removeItem('attemptedLoginCheck');
              attemptLogin();
            }, getNextRetryTimeoutMs());
          }
        })
        .catch((error) => {
          console.error(error);
          setError(true);
          setLoading(false);
          setTimeout(() => {
            sessionStorage.removeItem('attemptedLoginCheck');
            setError(false);
            setLoading(true);
            attemptLogin();
          }, getNextRetryTimeoutMs());
        });
    }
  });

  if (loading && !error) return <Loading />;
  if (error)
    return (
      <Error message="Something went wrong when trying to log in. Page will reattempt to log in shortly." />
    );

  return <React.Fragment>{component}</React.Fragment>;
};
